import {
  Button,
  CheckboxFieldController,
  Divider,
  SelectFieldController,
  Stack,
  TextInputFieldController,
} from '@fukuri-admins/ui/common';
import type { FormEventHandler } from 'react';
import type { CustomFieldDefinition } from '~/types/resources/CustomFieldDefinition';
import { SectionTitle } from '../../CreateClientForm/presentations/SectionTitle';
import { CustomFieldFormController } from './CustomFieldFormController';
import styles from './styles.module.scss';

type SelectOption = {
  value: string;
  label: string;
};

type FeatureFlagOption = {
  value: string;
  label: string;
};

type Props = {
  /** フォーム送信ハンドラ */
  onSubmit: FormEventHandler<HTMLFormElement>;
  /** キャンセルボタンクリックハンドラ */
  onCancel: React.ComponentProps<'button'>['onClick'];
  /** 送信ボタンの無効化フラグ（送信中 or mutation 中） */
  isButtonDisabled: boolean;
  /** 企業名（タイトル表示用） */
  clientName: string;
  /** 都道府県選択肢 */
  prefectureOptions: SelectOption[];
  /** 取引店舗選択肢 */
  partnerBranchOptions: SelectOption[];
  /** グループ選択肢 */
  groupOptions: SelectOption[];
  /** クライアント機能フラグ選択肢 */
  featureFlagOptions: FeatureFlagOption[];
  /** カスタムフィールド定義 */
  customFieldDefinitions: CustomFieldDefinition[];
};

/**
 * 利用企業編集フォーム Presentation（新版）
 *
 * CreateClientForm の Presentation をベースに、編集固有項目を merge した構成。
 * 会社情報 / 担当者情報 / 請求先情報の3セクションに加えて、
 * クライアント機能フラグがある場合のみプラン情報セクション（機能フラグのチェックボックス）が表示される。
 * カスタムフィールドが表示される。
 * placeholder は Figma（node-id=19882-94629）に準拠。
 * PEN-898 で仕様変更により利用開始日（desiredStartMonth）フィールドを削除した。
 * PEN-896 で仕様の項目表に合わせて、担当者情報セクションに電話番号（必須）・お電話がつながりやすい時間帯（平日）（任意）を追加した。
 * PEN-895 で仕様の項目表に合わせて、会社名・法人番号・外部企業ID・利用予定者数・担当者氏名・
 * メールアドレスを追加した。labelType・placeholder は CreateClientForm に揃えている。
 * PEN-911 で外部企業ID・グループの表示位置を Figma（node-id=19882-94634）・ClientDetail に合わせて、
 * 取引店舗の直後（カスタムフィールドの前）に修正した。
 * PEN-900 で、取引店舗・グループとも選択肢が0件のとき項目自体を非表示にするよう修正した。
 * 取引店舗は選択肢が1件以上あるときは常に必須（非表示のときはバリデーション対象外）となるため、
 * `partnerBranchOptions.length > 0` の条件ブロック内では常に `labelType: 'required'` を付与する。
 *
 * @param onSubmit - フォーム送信ハンドラ
 * @param onCancel - キャンセルボタンクリックハンドラ
 * @param isButtonDisabled - 送信ボタンの無効化フラグ
 * @param clientName - 企業名（タイトル表示用）
 * @param prefectureOptions - 都道府県選択肢
 * @param partnerBranchOptions - 取引店舗選択肢
 * @param groupOptions - グループ選択肢
 * @param featureFlagOptions - クライアント機能フラグ選択肢
 * @param customFieldDefinitions - カスタムフィールド定義
 */
export const EditClientFormPresentation: React.FC<Props> = ({
  onSubmit,
  onCancel,
  isButtonDisabled,
  clientName,
  prefectureOptions,
  partnerBranchOptions,
  groupOptions,
  featureFlagOptions,
  customFieldDefinitions,
}) => (
  <form onSubmit={onSubmit}>
    {/* フォームフィールド群 */}
    <Stack direction='column' gap={32} className={styles.fieldSections}>
      <h2 className={styles.formTitle}>{clientName}の編集</h2>

      {/* ── 会社情報 ── */}
      <Stack direction='column' gap={24} width={564}>
        <SectionTitle title='会社情報' />
        <Stack direction='column' gap={16}>
          <TextInputFieldController
            name='corporateNumber'
            fieldWrapperProps={{
              labelText: '法人番号',
            }}
            textInput={{ type: 'text', placeholder: '任意の文字列を入力してください' }}
          />
          <TextInputFieldController
            name='name'
            fieldWrapperProps={{
              labelType: 'required',
              labelText: '会社名',
            }}
            textInput={{ type: 'text', placeholder: '例）株式会社福利厚生' }}
          />
          <TextInputFieldController
            name='nameKana'
            fieldWrapperProps={{
              labelText: '会社名（フリガナ）',
            }}
            textInput={{ type: 'text', placeholder: '例）フクリコウセイ' }}
          />
          <TextInputFieldController
            name='representativeName'
            fieldWrapperProps={{
              labelText: '代表者氏名',
            }}
            textInput={{ type: 'text', placeholder: '例）福利 太郎' }}
          />
          <div className={styles.field172}>
            <TextInputFieldController
              name='postalCode'
              fieldWrapperProps={{
                labelText: '郵便番号（ハイフン抜きでご記載ください）',
              }}
              textInput={{ type: 'text', placeholder: '例）0000000' }}
            />
          </div>
          {/* 都道府県 + 市区町村 横並び（gap 24px、全体 564px） */}
          <Stack direction='row' gap={24} alignItems='flex-start'>
            <div className={styles.field172}>
              <SelectFieldController
                name='prefectureCode'
                fieldWrapperProps={{
                  labelText: '都道府県',
                }}
                select={{
                  placeholder: '都道府県を選択',
                  options: prefectureOptions,
                }}
              />
            </div>
            <div className={styles.cityField}>
              <TextInputFieldController
                name='city'
                fieldWrapperProps={{
                  labelText: '市区町村',
                }}
                textInput={{ type: 'text', placeholder: '例）○○市○○町' }}
              />
            </div>
          </Stack>
          <TextInputFieldController
            name='streetAddress'
            fieldWrapperProps={{
              labelText: '番地以降',
            }}
            textInput={{ type: 'text', placeholder: '例）１-２-３' }}
          />
          <div className={styles.employeeCountField}>
            <TextInputFieldController
              name='employeeCount'
              fieldWrapperProps={{
                labelType: 'required',
                labelText: '利用予定者数',
              }}
              textInput={{ type: 'number', placeholder: '100' }}
            />
          </div>
          {partnerBranchOptions.length > 0 && (
            <SelectFieldController
              name='partnerBranchId'
              fieldWrapperProps={{
                labelText: '取引店舗',
                labelType: 'required',
              }}
              select={{
                placeholder: '選択してください',
                options: partnerBranchOptions,
              }}
            />
          )}
          <TextInputFieldController
            name='externalClientId'
            fieldWrapperProps={{
              labelText: '外部企業ID',
            }}
            textInput={{ type: 'text', placeholder: '任意の文字列を入力してください' }}
          />
          {groupOptions.length > 0 && (
            <SelectFieldController
              name='groupId'
              fieldWrapperProps={{
                labelText: 'グループ',
              }}
              select={{
                placeholder: '選択してください',
                options: groupOptions,
                clearable: true,
              }}
            />
          )}
          {customFieldDefinitions.map(field => (
            <CustomFieldFormController
              key={field.id}
              name={`customFields.${field.label}`}
              field={field}
            />
          ))}
        </Stack>
      </Stack>

      {/* ── プラン情報（機能フラグがある場合のみ表示） ── */}
      {featureFlagOptions.length > 0 && (
        <>
          <Divider />
          <Stack direction='column' gap={24} width={564}>
            <SectionTitle title='プラン情報' />
            <Stack direction='column' gap={16}>
              <CheckboxFieldController
                name='clientFeatureFlags'
                options={featureFlagOptions}
                fieldWrapperProps={undefined}
              />
            </Stack>
          </Stack>
        </>
      )}

      <Divider />

      {/* ── 担当者情報 ── */}
      <Stack direction='column' gap={24} width={564}>
        <SectionTitle title='担当者情報' />
        <Stack direction='column' gap={16}>
          <TextInputFieldController
            name='contactPersonName'
            fieldWrapperProps={{
              labelType: 'required',
              labelText: '担当者氏名',
            }}
            textInput={{ type: 'text', placeholder: '例）福利 花子' }}
          />
          <TextInputFieldController
            name='contactPersonNameKana'
            fieldWrapperProps={{
              labelType: 'required',
              labelText: '担当者氏名（フリガナ）',
            }}
            textInput={{ type: 'text', placeholder: '例）フクリ ハナコ' }}
          />
          <TextInputFieldController
            name='contactPersonEmail'
            fieldWrapperProps={{
              labelType: 'required',
              labelText: 'メールアドレス',
            }}
            textInput={{ type: 'email', placeholder: '例）fukuri@example.com' }}
          />
          <TextInputFieldController
            name='contactPersonPhone'
            fieldWrapperProps={{
              labelType: 'required',
              labelText: '電話番号',
            }}
            textInput={{ type: 'tel', placeholder: '例）090-0000-0000' }}
          />
          <TextInputFieldController
            name='contactPreferredTime'
            fieldWrapperProps={{
              labelText: 'お電話がつながりやすい時間帯（平日）',
            }}
            textInput={{ type: 'text', placeholder: '例）10:00〜15:00' }}
          />
        </Stack>
      </Stack>

      <Divider />

      {/* ── 請求先情報 ── */}
      <Stack direction='column' gap={24} width={564}>
        <SectionTitle title='請求先情報' />
        <Stack direction='column' gap={16}>
          <TextInputFieldController
            name='billingContactName'
            fieldWrapperProps={{
              labelText: '請求担当者氏名',
            }}
            textInput={{ type: 'text', placeholder: '例）福利 花子' }}
          />
          <TextInputFieldController
            name='billingContactNameKana'
            fieldWrapperProps={{
              labelText: '請求担当者氏名（フリガナ）',
            }}
            textInput={{ type: 'text', placeholder: '例）フクリ ハナコ' }}
          />
          <TextInputFieldController
            name='billingEmail'
            fieldWrapperProps={{
              labelText: '請求書送付先メールアドレス',
            }}
            textInput={{ type: 'email', placeholder: '例）fukuri@example.com' }}
          />
        </Stack>
      </Stack>
    </Stack>

    {/* ボトムアクションバー（Figma: Admin / Parts / BottomActionButton） */}
    <div className={styles.bottomActionBar}>
      <Stack direction='row' gap={16} alignItems='center'>
        <Button type='button' variant='outline' size='md' onClick={onCancel}>
          キャンセル
        </Button>
        <Button type='submit' size='md' disabled={isButtonDisabled}>
          更新
        </Button>
      </Stack>
    </div>
  </form>
);
